import React, { useState, useEffect, useMemo, useRef } from 'react';
import { jwtFetch } from "./utils/apiClient";
import { Card, Row, Col, Form, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { format, subMonths, eachDayOfInterval, isSameDay, parseISO, } from 'date-fns';
import Menu from './Components/Menu';
import Header from './Components/Header';
import { config } from './Components/Config';
import { useNavigate } from 'react-router-dom';

// --- CONSTANTS & CONFIG ---
const COLORS = {
    resolved: '#1b8756', // Green
    created: '#fd7e14',  // Orange
    primary: '#0d6efd',  // Blue
    grid: '#e9ecef',
    text: '#6c757d'
};

// --- CUSTOM SVG CHART COMPONENT ---
const SimpleLineChart = ({ data, height = 300 }) => {
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const containerRef = useRef(null);
    const [width, setWidth] = useState(600);

    useEffect(() => {
        const handleResize = () => {
            if (containerRef.current) {
                setWidth(containerRef.current.offsetWidth);
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    if (!data || data.length === 0) return <div className="text-center p-5 text-muted">No data for chart</div>;

    const padding = { top: 20, right: 20, bottom: 50, left: 40 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Find Max Value for Y-Axis Scale
    const maxValue = Math.max(
        ...data.map(d => Math.max(d.created, d.resolved)),
        5 // Minimum scale
    );

    const getY = (value) => chartHeight - (value / maxValue) * chartHeight;
    const getX = (index) => (index / (data.length - 1)) * chartWidth;

    // Generate Path Strings
    const createdPath = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(d.created)}`).join(' ');
    const resolvedPath = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(d.resolved)}`).join(' ');

    return (
        <div ref={containerRef} style={{ width: '100%', height: height, position: 'relative' }}>
            <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`} style={{ overflow: 'visible' }}>
                <g transform={`translate(${padding.left}, ${padding.top})`}>
                    {/* Grid Lines (Y-Axis) */}
                    {[0, 0.25, 0.5, 0.75, 1].map((tick, i) => {
                        const y = chartHeight - (tick * chartHeight);
                        const value = Math.round(tick * maxValue);
                        return (
                            <g key={i}>
                                <line x1="0" y1={y} x2={chartWidth} y2={y} stroke={COLORS.grid} strokeDasharray="4 4" />
                                <text x="-10" y={y + 5} textAnchor="end" fontSize="10" fill={COLORS.text}>{value}</text>
                            </g>
                        );
                    })}

                    {/* Lines */}
                    <path d={createdPath} fill="none" stroke={COLORS.created} strokeWidth="2" />
                    <path d={resolvedPath} fill="none" stroke={COLORS.resolved} strokeWidth="2" />

                    {/* Data Points (Visible on Hover) */}
                    {data.map((d, i) => {
                        const x = getX(i);
                        const yCreated = getY(d.created);
                        const yResolved = getY(d.resolved);
                        const isHovered = hoveredIndex === i;

                        return (
                            <g key={i} onMouseEnter={() => setHoveredIndex(i)} onMouseLeave={() => setHoveredIndex(null)}>
                                {/* Invisible Hit Area */}
                                <rect x={x - (chartWidth / data.length / 2)} y="0" width={chartWidth / data.length} height={chartHeight} fill="transparent" />

                                <circle cx={x} cy={yCreated} r={isHovered ? 6 : 3} fill={COLORS.created} stroke="#fff" strokeWidth="2" />
                                <circle cx={x} cy={yResolved} r={isHovered ? 6 : 3} fill={COLORS.resolved} stroke="#fff" strokeWidth="2" />

                                {isHovered && (
                                    <g>
                                        <line x1={x} y1={0} x2={x} y2={chartHeight} stroke="#ddd" strokeDasharray="2 2" />
                                    </g>
                                )}
                            </g>
                        );
                    })}

                    {/* X-Axis Labels */}
                    {data.map((d, i) => {
                        // Show label only for specific intervals to avoid clutter
                        const showLabel = data.length > 20 ? i % 5 === 0 : true;
                        if (!showLabel) return null;
                        return (
                            <text key={i} x={getX(i)} y={chartHeight + 20} textAnchor="middle" fontSize="10" fill={COLORS.text} transform={`rotate(-45, ${getX(i)}, ${chartHeight + 20})`}>
                                {format(parseISO(d.date), 'MMM d')}
                            </text>
                        );
                    })}
                </g>
            </svg>

            {/* Tooltip Overlay */}
            {hoveredIndex !== null && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    border: '1px solid #ddd',
                    padding: '10px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                    pointerEvents: 'none'
                }}>
                    <strong>{format(parseISO(data[hoveredIndex].date), 'EEE, MMM d, yyyy')}</strong>
                    <div style={{ color: COLORS.created }}>Created: <strong>{data[hoveredIndex].created}</strong></div>
                    <div style={{ color: COLORS.resolved }}>Resolved: <strong>{data[hoveredIndex].resolved}</strong></div>
                </div>
            )}
        </div>
    );
};

// --- MAIN COMPONENT ---
const SupportReport = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [tickets, setTickets] = useState([]);
    const [resolvedTickets, setResolvedTickets] = useState([]);
    const [backendMetrics, setBackendMetrics] = useState(null);

    // Filters State
    const [dateRange, setDateRange] = useState([subMonths(new Date(), 1), new Date()]);
    const [startDate, endDate] = dateRange;

    const [selectedQueues, setSelectedQueues] = useState([]);
    const [selectedAgents, setSelectedAgents] = useState([]);
    const [selectedPriorities, setSelectedPriorities] = useState([]);
    const [selectedResolutions, setSelectedResolutions] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [activeTab, setActiveTab] = useState('created');

    // Options State
    const [queueOptions, setQueueOptions] = useState([]);
    const [agentOptions, setAgentOptions] = useState([]);
    const [tagOptions, setTagOptions] = useState([]);
    const navigate = useNavigate();
    const priorityOptions = [
        { value: 'LOW', label: 'Low' },
        { value: 'NORMAL', label: 'Normal' },
        { value: 'URGENT', label: 'Urgent' },
        { value: 'EMERGENCY', label: 'Emergency' }
    ];
    const resolutionOptions = [
        { label: 'No Resolution', value: 'NO_RESOLUTION' },
        { label: 'Call', value: 'CALL' },
        { label: 'Chat', value: 'CHAT' },
        { label: 'Email', value: 'EMAIL' },
        { label: 'Internal', value: 'INTERNAL' },
        { label: 'Resolved', value: 'RESOLVED' },
        { label: 'User Resolved', value: 'USER_RESOLVED' },
        { label: 'WhatsApp', value: 'WA' }
    ];

    // Fetch Initial Options
    useEffect(() => {
        fetchOptions();
    }, []);

    const fetchOptions = async () => {
        try {
            // Fetch Agents
            const userRes = await jwtFetch(`${config.ENDPOINT}get/user`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_department: 'Support' })
            });
            const userJson = await userRes.json();
            if (userJson.status === 200) {
                setAgentOptions(userJson.data.map(u => ({ value: u.user_keyid, label: u.full_name, email: u.email_address })));
            }

            // Fetch Queues
            const queueRes = await fetch(`${config.SUPPORT_ENDPOINT}queues?limit=false`);
            const queueJson = await queueRes.json();
            if (queueJson.success) {
                setQueueOptions(queueJson.data.map(q => ({ value: q.id, label: q.name })));
            }

            // Fetch Tags
            const tagRes = await fetch(`${config.SUPPORT_ENDPOINT}tags?limit=false`);
            const tagJson = await tagRes.json();
            if (tagJson.success) {
                setTagOptions(tagJson.data.map(t => ({ value: t.id, label: t.name, color: t.color })));
            }

        } catch (err) {
            console.error("Error fetching options:", err);
        }
    };

    // Fetch Report Data
    useEffect(() => {
        setCurrentPage(1); // Reset pagination on filter change
        fetchReportData();
    }, [dateRange, selectedQueues, selectedAgents, selectedPriorities, selectedResolutions, selectedTags]);

    const fetchReportData = async () => {
        if (!startDate || !endDate) return;

        setIsLoading(true);
        try {
            // Construct Query Params
            const params = new URLSearchParams({
                startDate: format(startDate, 'yyyy-MM-dd'),
                endDate: format(endDate, 'yyyy-MM-dd')
            });

            if (selectedQueues.length) params.append('queueId', selectedQueues.map(q => q.value).join(','));
            if (selectedAgents.length) params.append('userEmail', selectedAgents.map(a => a.label === 'Support Team' ? 'support@point-star.com' : (a.email || a.value)).join(','));
            if (selectedPriorities.length) params.append('priority', selectedPriorities.map(p => p.value).join(','));
            if (selectedResolutions.length) params.append('resolution', selectedResolutions.map(r => r.value).join(','));
            if (selectedTags.length) params.append('tags', selectedTags.map(t => t.value).join(','));

            const response = await fetch(`${config.SUPPORT_ENDPOINT}tickets/report?${params.toString()}`);
            const result = await response.json();

            if (result.success) {
                setTickets(result.data.tickets);
                setResolvedTickets(result.data.resolvedTickets || []);
                setBackendMetrics(result.data.metrics);
            }
        } catch (error) {
            console.error("Error fetching report data:", error);
        } finally {
            setIsLoading(false);
        }
    };
    // --- AGGREGATION LOGIC ---
    const chartData = useMemo(() => {
        if (!tickets || !resolvedTickets || !startDate || !endDate) return [];

        // Chart Data Aggregation
        const days = eachDayOfInterval({
            start: startDate,
            end: endDate
        });

        return days.map(day => {
            return {
                date: format(day, 'yyyy-MM-dd'),
                created: tickets.filter(t => isSameDay(new Date(t.createdAt), day)).length,
                resolved: resolvedTickets.filter(t => t.closedAt && isSameDay(new Date(t.closedAt), day)).length
            };
        });

    }, [tickets, resolvedTickets, dateRange]);

    const formatDuration = (ms) => {
        if (!ms) return '-';

        const totalSeconds = Math.floor(ms / 1000);
        const days = Math.floor(totalSeconds / (3600 * 24));
        const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        const parts = [];
        if (days > 0) parts.push(`${days}d`);
        if (hours > 0) parts.push(`${hours}h`);
        if (minutes > 0) parts.push(`${minutes}m`);
        if (seconds > 0) parts.push(`${seconds}s`);

        return parts.length > 0 ? parts.join(' ') : '0s';
    };

    const truncateText = (value, maxLength = 45) => {
        if (!value || typeof value !== "string") return value || "";
        if (value.length <= maxLength) return value;
        return `${value.slice(0, maxLength).trim()}...`;
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'NEW': return '#1b8756'; // Warning/Yellow
            case 'IN_PROGRESS': return '#0d6efd'; // Primary/Blue
            case 'ON_HOLD': return '#6c757d'; // Orange
            case 'SOLVED': return '#93ace6'; // Success/Green
            case 'CLOSED': return '#8410DCFF'; // Secondary/Grey
            case 'INFORMATION_REQUESTED': return '#ffc108'; // Info/Blue
            default: return '#6c757d';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'EMERGENCY': return '#dc3545'; // Danger/Red
            case 'URGENT': return '#fd7e14'; // Orange
            case 'NORMAL': return '#0d6efd'; // Blue
            case 'LOW': return '#6c757d'; // Grey
            default: return '#0d6efd';
        }
    };

    // Active Data Logic
    const filteredResolvedTickets = useMemo(() => {
        return resolvedTickets ? resolvedTickets.filter(t => t.status === 'CLOSED' && t.closedAt) : [];
    }, [resolvedTickets]);

    const activeData = useMemo(() => {
        if (activeTab === 'resolved') {
            return filteredResolvedTickets;
        }
        return tickets || [];
    }, [activeTab, tickets, filteredResolvedTickets]);

    // Pagination Logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = activeData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(activeData.length / itemsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const renderPaginationItems = () => {
        const items = [];

        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) {
                items.push(i);
            }
        } else {
            if (currentPage <= 4) {
                for (let i = 1; i <= 5; i++) items.push(i);
                items.push('...');
                items.push(totalPages);
            } else if (currentPage >= totalPages - 3) {
                items.push(1);
                items.push('...');
                for (let i = totalPages - 4; i <= totalPages; i++) items.push(i);
            } else {
                items.push(1);
                items.push('...');
                items.push(currentPage - 1);
                items.push(currentPage);
                items.push(currentPage + 1);
                items.push('...');
                items.push(totalPages);
            }
        }

        return items.map((item, index) => (
            <li key={index} className="page-item">
                {item === '...' ? (
                    <span className="page-link border-0 text-secondary bg-transparent" style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>...</span>
                ) : (
                    <button
                        className={`page-link border-0 rounded-circle d-flex align-items-center justify-content-center ${currentPage === item ? 'bg-gold text-white shadow-sm' : 'text-secondary bg-transparent'}`}
                        style={{ width: '32px', height: '32px' }}
                        onClick={() => paginate(item)}
                    >
                        {item}
                    </button>
                )}
            </li>
        ));
    };

    return (
        <div id="support-report">
            <div className="container-fluid p-0">
                <div className="row g-0 mh-100vh">
                    <div className="col-md-2 border-end">
                        <div className="d-md-flex d-none align-items-center justify-content-center p-1 h-48">
                            <a className="h-100" href="/dashboard"><img className="h-100" src="/cuancuan.png" alt="PointStar" /></a>
                        </div>
                        <hr className="m-0" />
                        <Menu active="support-report" />
                    </div>
                    <div className="col-md-10">
                        <Header />
                        <hr className="m-0" />
                        <div className="main bg-light p-4">

                            {/* --- TITLE & FILTERS --- */}
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h3 className="fw-bold m-0">Report Support Ticket</h3>
                                <div className="d-flex align-items-center gap-2">
                                    <span className="text-muted">Period:</span>
                                    <DatePicker
                                        selectsRange={true}
                                        startDate={startDate}
                                        endDate={endDate}
                                        onChange={(update) => {
                                            setDateRange(update);
                                        }}
                                        className="form-control form-control-sm w-200 text-center"
                                        dateFormat="yyyy-MM-dd"
                                        placeholderText="Select date range"
                                        isClearable={false}
                                    />
                                </div>
                            </div>

                            {/* --- FILTER ROW --- */}
                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Body className="p-3">
                                    <Row className="g-2">
                                        <Col md={2}>
                                            <Select
                                                isMulti
                                                placeholder="Queues"
                                                options={queueOptions}
                                                value={selectedQueues}
                                                onChange={setSelectedQueues}
                                                className="text-sm"
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <Select
                                                isMulti
                                                placeholder="Agents"
                                                options={agentOptions}
                                                value={selectedAgents}
                                                onChange={setSelectedAgents}
                                                className="text-sm"
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <Select
                                                isMulti
                                                placeholder="Tags"
                                                options={tagOptions}
                                                value={selectedTags}
                                                onChange={setSelectedTags}
                                                className="text-sm"
                                            />
                                        </Col>
                                        <Col md={2}>
                                            <Select
                                                isMulti
                                                placeholder="Resolutions"
                                                options={resolutionOptions}
                                                value={selectedResolutions}
                                                onChange={setSelectedResolutions}
                                                className="text-sm"
                                            />
                                        </Col>
                                        <Col md={2}>
                                            <Select
                                                isMulti
                                                placeholder="Priorities"
                                                options={priorityOptions}
                                                value={selectedPriorities}
                                                onChange={setSelectedPriorities}
                                                className="text-sm"
                                            />
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            {isLoading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-2 text-muted">Generating Report...</p>
                                </div>
                            ) : (
                                <>
                                    {/* --- MAIN CHART --- */}
                                    <Card className="border-0 shadow-sm mb-4">
                                        <Card.Body>
                                            <div className="d-flex align-items-center justify-content-center gap-4 mb-4">
                                                <div className="d-flex align-items-center gap-2">
                                                    <div style={{ width: 20, height: 4, backgroundColor: COLORS.resolved }}></div>
                                                    <span className="small fw-bold text-secondary">Resolved tickets</span>
                                                </div>
                                                <div className="d-flex align-items-center gap-2">
                                                    <div style={{ width: 20, height: 4, backgroundColor: COLORS.created }}></div>
                                                    <span className="small fw-bold text-secondary">Created tickets</span>
                                                </div>
                                            </div>

                                            <div style={{ minHeight: '300px' }}>
                                                {chartData && <SimpleLineChart data={chartData} height={300} />}
                                            </div>
                                        </Card.Body>
                                    </Card>

                                    {/* --- METRICS GRID --- */}
                                    <Row className="g-3 mb-4">
                                        <Col md={4}>
                                            <Card className="border-0 shadow-sm h-100">
                                                <Card.Body>
                                                    <h6 className="text-dark fw-bold mb-3">Resolved Ticket</h6>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <i className="bi bi-check-lg text-success fs-4"></i>
                                                        <span className="fs-3 fw-bold text-success">{backendMetrics?.totalResolved || 0}</span>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col md={4}>
                                            <Card className="border-0 shadow-sm h-100">
                                                <Card.Body>
                                                    <h6 className="text-dark fw-bold mb-3">Created Ticket</h6>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <i className="bi bi-asterisk text-warning fs-4"></i>
                                                        <span className="fs-3 fw-bold text-warning">{backendMetrics?.totalCreated || 0}</span>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col md={4}>
                                            <Card className="border-0 shadow-sm h-100">
                                                <Card.Body>
                                                    <h6 className="text-dark fw-bold mb-3">Average Solved Time</h6>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <i className="bi bi-clock-history text-secondary fs-4"></i>
                                                        <span className="fs-4 fw-bold text-secondary">{formatDuration(backendMetrics?.avgSolvedTime)}</span>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>

                                    {/* --- TICKET TABLE --- */}
                                    <Card className="border-0 shadow-sm">
                                        <Card.Body>
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h5 className="fw-bold mb-0">Ticket List</h5>
                                                <ul className="nav nav-pills">
                                                    <li className="nav-item">
                                                        <button
                                                            className={`nav-link btn-sm ${activeTab === 'created' ? 'bg-gold text-dark' : 'text-secondary'}`}
                                                            onClick={() => { setActiveTab('created'); setCurrentPage(1); }}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            Created ({tickets ? tickets.length : 0})
                                                        </button>
                                                    </li>
                                                    <li className="nav-item ms-2">
                                                        <button
                                                            className={`nav-link btn-sm ${activeTab === 'resolved' ? 'bg-gold text-dark' : 'text-secondary'}`}
                                                            onClick={() => { setActiveTab('resolved'); setCurrentPage(1); }}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            Resolved ({filteredResolvedTickets ? filteredResolvedTickets.length : 0})
                                                        </button>
                                                    </li>
                                                </ul>
                                            </div>
                                            <div className="table-responsive">
                                                <table className="table table-hover">
                                                    <thead>
                                                        <tr>
                                                            <th className="bg-gold text-center">Ticket ID</th>
                                                            <th className="bg-gold text-center">Subject</th>
                                                            <th className="bg-gold text-center">Requester</th>
                                                            <th className="bg-gold text-center">Status</th>
                                                            <th className="bg-gold text-center">Agent</th>
                                                            <th className="bg-gold text-center">Resolution</th>
                                                            <th className="bg-gold text-center">Priority</th>
                                                            <th className="bg-gold text-center">{activeTab === 'resolved' ? 'Resolved At' : 'Created At'}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {currentItems.length > 0 ? (
                                                            currentItems.map(ticket => (
                                                                <tr style={{ cursor: 'pointer' }} onClick={() => navigate(`/ticket/${ticket.id}`)} onContextMenu={(e) => {
                                                                    e.preventDefault();
                                                                    window.open(`/ticket/${ticket.id}`, '_blank');
                                                                }} key={ticket.id}>
                                                                    <td valign="middle" className="text-center"><strong>#{ticket.code || ticket.id.substring(0, 8)}</strong></td>
                                                                    <td valign="middle" title={ticket.title || ''}><strong>{truncateText(ticket.title)}</strong></td>
                                                                    <td valign="middle">
                                                                        <div>{ticket.receiveName || ticket.userName || 'Unknown'}</div>
                                                                        <div className="text-muted small">{ticket.receiveEmail || ticket.userEmail}</div>
                                                                    </td>
                                                                    <td valign="middle" className="text-center"><span className={`badge ${ticket?.status === "INFORMATION_REQUESTED" ? "text-dark" : "text-white"}`} style={{ backgroundColor: getStatusColor(ticket?.status) }}>{ticket?.status?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</span></td>
                                                                    <td valign="middle" className="text-center">{ticket.userName || '-'}</td>
                                                                    <td valign="middle" className="text-center">{ticket.resolution || '-'}</td>
                                                                    <td valign="middle" className="text-center"> <span className="badge" style={{ backgroundColor: getPriorityColor(ticket?.priority) }}>{ticket?.priority?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</span></td>
                                                                    <td valign="middle" className="text-center">{format(new Date(activeTab === 'resolved' ? ticket.closedAt : ticket.createdAt), 'dd MMM yyyy HH:mm')}</td>
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                <td colSpan="8" className="text-center py-3">No tickets found</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>

                                            {/* Pagination Controls */}
                                            {totalPages > 1 && (
                                                <nav className="d-flex justify-content-center mt-3">
                                                    <ul className="pagination pagination-sm mb-0 gap-1">
                                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                            <button className="page-link border-0 text-secondary bg-transparent" onClick={() => paginate(currentPage - 1)}>
                                                                <i className="bi bi-chevron-left" />
                                                            </button>
                                                        </li>
                                                        {renderPaginationItems()}
                                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                            <button className="page-link border-0 text-secondary bg-transparent" onClick={() => paginate(currentPage + 1)}>
                                                                <i className="bi bi-chevron-right" />
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </nav>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SupportReport;




